/**
 * sharedGet — Jun 2026 · Slow Call Audit (phases 1 & 2)
 *
 * De-duplication for the GETs that several screens (or several components on
 * one screen) each asked for independently: `/auth/me`, `/billing/me`,
 * `/admin/me`, the messages unread count, the compliance due badges, the
 * business list, the dashboard widgets and the investor panels. Before this, a
 * single Dashboard load fired 28 requests across 15 endpoints and the Plan
 * page asked for investor-visibility three times.
 *
 * Behaviour mirrors `fetchBusinessWithRetry`: concurrent callers share one
 * in-flight promise, and a resolved response is reused until its TTL expires.
 *
 * Freshness rules:
 *   - Identity-shaped endpoints (`auth/me`, `billing/me`, `admin/me`) are
 *     `sticky`: cached for the session and refreshed explicitly, on login /
 *     logout (`clearSharedCache`) or with `{ force: true }` after a mutation.
 *   - Everything else carries a short TTL — long enough to collapse a mount
 *     burst (including React StrictMode's replay), short enough that a poller
 *     still sees every tick.
 *   - ANY non-GET request through the same api client drops every non-sticky
 *     entry, so a save / create / delete is never followed by a stale read.
 */
const inflight = new Map();
const cache = new Map();
const wired = new WeakSet();
// Bumped by every invalidation. A GET that was already in flight when a write
// landed must not repopulate the cache with pre-write data, so each request
// remembers the epoch it started in and only caches if nothing invalidated
// in the meantime.
let epoch = 0;

// TTLs, grouped so the intent of each number is readable at the call site.
export const TTL = {
  // Collapses a mount burst only. Used by pollers (health pills, badges).
  BURST: 5_000,
  // A page's own widgets + a navigation back within a few seconds.
  SHORT: 10_000,
  // Slow-moving digests and per-user preferences.
  LONG: 60_000,
};

function clone(res) {
  return res && typeof res === "object" && res.data && typeof res.data === "object"
    ? { ...res, data: Array.isArray(res.data) ? [...res.data] : { ...res.data } }
    : res;
}

export function invalidateShared(url) {
  epoch += 1;
  cache.delete(url);
  inflight.delete(url);
}

export function invalidateSharedPrefix(prefix) {
  epoch += 1;
  [...cache.keys()].filter((k) => k.startsWith(prefix)).forEach((k) => cache.delete(k));
  [...inflight.keys()].filter((k) => k.startsWith(prefix)).forEach((k) => inflight.delete(k));
}

// Called on logout and on an identity swap: nothing from the previous session
// may leak into the next one.
export function clearSharedCache() {
  epoch += 1;
  cache.clear();
  inflight.clear();
}

// Any write invalidates every cached list/detail read. Identity entries are
// sticky and survive — they only change on login/logout.
export function invalidateAfterWrite() {
  epoch += 1;
  [...cache.entries()].forEach(([k, v]) => {
    if (!v.sticky) cache.delete(k);
  });
  inflight.clear();
}

function wireInvalidation(api) {
  if (!api?.interceptors?.response || wired.has(api)) return;
  wired.add(api);
  api.interceptors.response.use((res) => {
    if (String(res?.config?.method || "get").toLowerCase() !== "get") invalidateAfterWrite();
    return res;
  });
}

export function sharedGet(api, url, { ttlMs = TTL.SHORT, force = false, sticky = false } = {}) {
  wireInvalidation(api);
  if (force) invalidateShared(url);

  const hit = cache.get(url);
  if (hit && (hit.sticky || Date.now() - hit.at < ttlMs)) return Promise.resolve(clone(hit.res));

  const pending = inflight.get(url);
  if (pending) return pending.then(clone);

  const startedAt = epoch;
  let p;
  const settle = () => {
    if (inflight.get(url) === p) inflight.delete(url);
  };
  p = api.get(url).then(
    (res) => {
      // Something invalidated while this was in flight (almost always a
      // write) — the response may predate it, so don't cache it.
      if (epoch === startedAt) cache.set(url, { at: Date.now(), res, sticky });
      settle();
      return res;
    },
    (err) => {
      settle();
      throw err;
    },
  );
  inflight.set(url, p);
  return p.then(clone);
}

// --- Identity (session-long) -------------------------------------------------

export function fetchAuthMe(api, opts = {}) {
  return sharedGet(api, "/auth/me", { sticky: true, ...opts });
}

export function fetchBillingMe(api, opts = {}) {
  return sharedGet(api, "/billing/me", { sticky: true, ...opts });
}

export function fetchAdminMe(api, opts = {}) {
  return sharedGet(api, "/admin/me", { sticky: true, ...opts });
}

// --- Polled badges ----------------------------------------------------------

export function fetchUnreadCount(api, { businessId, ...opts } = {}) {
  const url = businessId ? `/messages/unread-count?business_id=${businessId}` : "/messages/unread-count";
  return sharedGet(api, url, { ttlMs: TTL.SHORT, ...opts });
}

export function invalidateUnreadCount() {
  invalidateSharedPrefix("/messages/unread-count");
}

// --- Lists & summaries ------------------------------------------------------

// The plan list is read by the Dashboard, Compliance, Grants, Settings,
// Integrations and Template detail. Writes (create / clone / delete /
// activate) invalidate it via the interceptor above.
export function fetchBusinesses(api, opts = {}) {
  return sharedGet(api, "/businesses", { ttlMs: TTL.SHORT, ...opts });
}

export function fetchActiveSummary(api, opts = {}) {
  return sharedGet(api, "/businesses/active-summary", { ttlMs: TTL.SHORT, ...opts });
}
